import { all, put, select, delay } from 'redux-saga/effects';
import {
    fetchNumberOfActivePlayers,
    fetchNumberOfActivePlayersFail,
    fetchNumberOfActivePlayersSuccess,
    fetchNumberOfEventsForTheLastDay,
    fetchNumberOfEventsForTheLastDayFail,
    fetchNumberOfEventsForTheLastDaySuccess,
    fetchNumberOfPlayers,
    fetchNumberOfPlayersFail,
    fetchNumberOfPlayersSuccess,
    fetchNumberOfPlayersWithOpenAlerts,
    fetchNumberOfPlayersWithOpenAlertsFail,
    fetchNumberOfPlayersWithOpenAlertsSuccess,
    fetchOpenAlertsPerRuleCategory,
    fetchOpenAlertsPerRuleCategoryFail,
    fetchOpenAlertsPerRuleCategorySuccess,
    fetchOverallSystemRisk,
    fetchOverallSystemRiskFail,
    fetchOverallSystemRiskSuccess,
    fetchRegistrationsPerMonth,
    fetchRegistrationsPerMonthFail,
    fetchRegistrationsPerMonthSuccess,
    fetchTopRiskyPlayers,
    fetchTopRiskyPlayersFail,
    fetchTopRiskyPlayersSuccess,
    fetchWorldmap,
    fetchWorldmapFail,
    fetchWorldmapSuccess
} from './actions';
import {
    getNumberOfActivePlayers,
    getNumberOfEventsForTheLastDay,
    getNumberOfPlayers,
    getNumberOfPlayersWithOpenAlerts,
    getOpenAlertsPerRuleCategory,
    getOverallSystemRisk,
    getRegistrationsPerMonth,
    getTopRiskyPlayers,
    getWorldmap
} from './stats.backend';
import { makeErrorPayload } from '../../../../shared/helpers/payload.helper';
import { setErrorToast } from '../../../../shared/toast/redux/actions';
import {
    getSelectedBrandId,
    getSelectedJurisdictionCode
} from '../../../../jurisdiction/redux/selectors';

export function* fetchStatsSaga(action) {
    yield delay(300);
    const jurisdiction = yield select(getSelectedJurisdictionCode);
    const brandId = yield select(getSelectedBrandId);

    // The server reads brandId (StatsFilterDTO); a brand parameter would be ignored.
    // The payload of FETCH_STATS (clearOldStats) is for the reducer and is not sent.
    const params = { jurisdiction, brandId };

    if (shouldFetchStats()) {
        yield put(fetchNumberOfActivePlayers(params));
        yield put(fetchNumberOfEventsForTheLastDay(params));
        yield put(fetchNumberOfPlayers(params));
        yield put(fetchNumberOfPlayersWithOpenAlerts(params));
        yield put(fetchOpenAlertsPerRuleCategory(params));
        yield put(fetchOverallSystemRisk(params));
        yield put(fetchRegistrationsPerMonth(params));
        yield put(fetchTopRiskyPlayers(params));
        yield put(fetchWorldmap(params));
    }
}

export function* fetchWorldmapSaga(action) {
    try {
        const response = yield getWorldmap(action.payload);

        yield put(fetchWorldmapSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchWorldmapFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchNumberOfPlayersSaga(action) {
    try {
        const response = yield getNumberOfPlayers(action.payload);
        yield put(fetchNumberOfPlayersSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchNumberOfPlayersFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchNumberOfPlayersWithOpenAlertsSaga(action) {
    try {
        const response = yield getNumberOfPlayersWithOpenAlerts(action.payload);
        yield put(fetchNumberOfPlayersWithOpenAlertsSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchNumberOfPlayersWithOpenAlertsFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchNumberOfActivePlayersSaga(action) {
    try {
        const response = yield getNumberOfActivePlayers(action.payload);

        yield put(fetchNumberOfActivePlayersSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchNumberOfActivePlayersFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchNumberOfEventsForTheLastDaySaga(action) {
    try {
        const [withdrawalResponse, depositResponse, betResponse] = yield all([
            getNumberOfEventsForTheLastDay({
                ...action.payload,
                eventType: 'WITHDRAWAL'
            }),
            getNumberOfEventsForTheLastDay({
                ...action.payload,
                eventType: 'DEPOSIT'
            }),
            getNumberOfEventsForTheLastDay({
                ...action.payload,
                eventType: 'BET'
            })
        ]);

        const actionPayload = {
            numberOfDepositsForTheLastDay: depositResponse.data,
            numberOfWithdrawalsForTheLastDay: withdrawalResponse.data,
            numberOfBetsForTheLastDay: betResponse.data
        };

        yield put(fetchNumberOfEventsForTheLastDaySuccess(actionPayload));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchNumberOfEventsForTheLastDayFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchTopRiskyPlayersSaga(action) {
    try {
        const response = yield getTopRiskyPlayers(action.payload);

        yield put(fetchTopRiskyPlayersSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchTopRiskyPlayersFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchRegistrationsPerMonthSaga(action) {
    try {
        const response = yield getRegistrationsPerMonth(action.payload);

        yield put(fetchRegistrationsPerMonthSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchRegistrationsPerMonthFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchOverallSystemRiskSaga(action) {
    try {
        const response = yield getOverallSystemRisk(action.payload);

        yield put(fetchOverallSystemRiskSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchOverallSystemRiskFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchOpenAlertsPerRuleCategorySaga(action) {
    try {
        const response = yield getOpenAlertsPerRuleCategory(action.payload);

        yield put(fetchOpenAlertsPerRuleCategorySuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchOpenAlertsPerRuleCategoryFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

// todo: HACK, should be removed
const shouldFetchStats = () => window.location.pathname === '/dashboard';
